/**
 * Goods-receipt detail — `/purchasing/grns/:id`. A posted GRN is immutable,
 * so this screen is read-only: header facts, per-line quantities and exact
 * inventory values, and the stock-movement / goods-received-event ids each
 * posted line produced.
 */
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatDateTime, formatQuantity } from "@/lib/format";

import { useGrnDetail, usePurchaseOrderDetail } from "../queries";
import { useVariantSkus, useWarehouseNames } from "../components/lookup-names";
import { AMOUNT_SCALE, QTY_SCALE, addDecimals } from "../decimal";
import { enumLabel } from "../labels";

export interface GrnDetailPageProps {
  grnId: string;
  canReadVariants?: boolean;
  canReadWarehouses?: boolean;
  canReadPurchaseOrders?: boolean;
  onOpenPurchaseOrder: (purchaseOrderId: string) => void;
  onBack: () => void;
}

export function GrnDetailPage({
  grnId,
  canReadVariants = true,
  canReadWarehouses = true,
  canReadPurchaseOrders = true,
  onOpenPurchaseOrder,
  onBack,
}: GrnDetailPageProps) {
  const { t, i18n } = useTranslation();
  const detail = useGrnDetail(grnId);
  const grn = detail.data;
  const { variantSku } = useVariantSkus(canReadVariants);
  const { warehouseName } = useWarehouseNames(canReadWarehouses);
  // The GRN carries no currency of its own; it inherits the order's.
  const order = usePurchaseOrderDetail(
    canReadPurchaseOrders && grn !== undefined ? grn.purchase_order_id : undefined,
  );
  const currency = order.data?.data.currency ?? "SAR";

  const renderQty = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QTY_SCALE);
    } catch {
      return value;
    }
  };

  if (detail.isLoading) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (detail.isError || grn === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("purchasing.grnDetail.loadError", "This goods receipt could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  const totalValue = addDecimals(
    grn.lines.map((line) => line.inventory_value),
    AMOUNT_SCALE,
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <span dir="ltr" className="font-mono">
              {grn.grn_number}
            </span>
            <Badge variant="success">{enumLabel(t, "grnStatus", grn.status)}</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            {warehouseName(grn.warehouse_id)} ·{" "}
            {(() => {
              try {
                return formatDateTime(grn.received_at, i18n.language);
              } catch {
                return grn.received_at;
              }
            })()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canReadPurchaseOrders ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenPurchaseOrder(grn.purchase_order_id)}
            >
              {t("purchasing.grnDetail.openPo", "Open purchase order")}
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onBack}>
            {t("purchasing.grnDetail.backToList", "Back to goods receipts")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.grnDetail.linesTitle", "Received lines")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th scope="col" className="p-2 text-start">
                  #
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("purchasing.grnDetail.columns.variant", "Variant")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("purchasing.grnDetail.columns.batch", "Batch")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("purchasing.grnDetail.columns.received", "Received")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("purchasing.grnDetail.columns.accepted", "Accepted")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("purchasing.grnDetail.columns.rejected", "Rejected")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("purchasing.grnDetail.columns.unitCost", "Unit cost")}
                </th>
                <th scope="col" className="p-2 text-end">
                  {t("purchasing.grnDetail.columns.value", "Inventory value")}
                </th>
                <th scope="col" className="p-2 text-start">
                  {t("purchasing.grnDetail.columns.movement", "Stock movement")}
                </th>
              </tr>
            </thead>
            <tbody>
              {grn.lines.map((line) => (
                <tr key={line.id} className="border-b">
                  <td className="p-2 text-muted-foreground">{line.line_number}</td>
                  <td className="p-2">
                    <span dir="ltr" className="font-mono">
                      {variantSku(line.variant_id)}
                    </span>
                  </td>
                  <td className="p-2">
                    {line.batch_number === undefined ? (
                      <span className="text-muted-foreground">&mdash;</span>
                    ) : (
                      <span dir="ltr" className="font-mono text-xs">
                        {line.batch_number}
                      </span>
                    )}
                  </td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQty(line.qty_received)}
                  </td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQty(line.qty_accepted)}
                  </td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQty(line.qty_rejected)}
                  </td>
                  <td dir="ltr" className="p-2 text-end tabular-nums">
                    {renderQty(line.unit_cost)}
                  </td>
                  <td className="p-2 text-end">
                    <MoneyDisplay value={{ amount: line.inventory_value, currency }} />
                  </td>
                  <td className="p-2">
                    {line.stock_movement_id === undefined ? (
                      <span className="text-muted-foreground">&mdash;</span>
                    ) : (
                      <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {line.stock_movement_id}
                      </code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-2 text-end font-medium" colSpan={7}>
                  {t("purchasing.grnDetail.totalValue", "Total received value")}
                </td>
                <td className="p-2 text-end font-medium" data-testid="grn-total-value">
                  <MoneyDisplay value={{ amount: totalValue, currency }} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.grnDetail.eventsTitle", "Business events")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {grn.lines.filter((line) => line.goods_received_event_id !== undefined).length === 0 ? (
            <p className="text-muted-foreground">
              {t("purchasing.grnDetail.noEvents", "No goods-received events on this receipt.")}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {grn.lines
                .filter((line) => line.goods_received_event_id !== undefined)
                .map((line) => (
                  <li key={line.id} className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">goods_received</Badge>
                    <span dir="ltr" className="font-mono">
                      {variantSku(line.variant_id)}
                    </span>
                    <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {line.goods_received_event_id}
                    </code>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
